package info.rezident.app.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import info.rezident.app.data.auth.AuthRepository
import info.rezident.app.seo.*
import info.rezident.app.ui.components.sections.headerLogoStyle
import info.rezident.app.ui.components.widgets.GoogleSignInButton
import info.rezident.app.ui.components.widgets.InputField
import info.rezident.app.ui.components.widgets.Spacer
import info.rezident.app.ui.theme.AppTheme
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.AutoComplete
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

@Page("conectare")
@Composable
fun AuthPage(isOnboarding: Boolean = false) {
    var email by remember { mutableStateOf("") }
    var emailError by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var passwordError by remember { mutableStateOf("") }
    var isLoginMode by remember { mutableStateOf(false) }
    val viewModel = remember { AuthViewModel() }
    val authUiState by viewModel.authUiState.collectAsState()

    val title = "Conectare Cont rezident.info - salvează rezultatele quiz-ului"
    val description =
        "Intră în contul tău rezident.info pentru a salva rezultatele, relua quiz-ul și accesa recomandările complete."
    val canonicalUrl = "https://rezident.info/conectare"
    val ogImageUrl = "https://rezident.info/assets/og/og-image.png"

    Seo(title = title, description = description)

    LaunchedEffect(Unit) {
        document.setCanonicalUrl(canonicalUrl)
        document.setMetaNameTag(name = "robots", content = "noindex, follow")
        document.setMetaNameTag(name = "twitter:card", content = "summary_large_image")
        document.setMetaNameTag(name = "twitter:title", content = title)
        document.setMetaNameTag(name = "twitter:description", content = description)
        document.setMetaNameTag(name = "twitter:image", content = ogImageUrl)

        document.setOpenGraphTag(property = "og:title", content = title)
        document.setOpenGraphTag(property = "og:description", content = description)
        document.setOpenGraphTag(property = "og:url", content = canonicalUrl)
        document.setOpenGraphTag(property = "og:image", content = ogImageUrl)
    }

    val authJsonLd: String = """
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "url": "https://rezident.info/conectare",
          "name": "Conectare - rezident.info",
          "inLanguage": "ro-RO",
          "isPartOf": {
            "@id": "https://rezident.info/#website"
          }
        }
    """.trimIndent()

    ApplicationLdJson(authJsonLd)

    val pageContext = rememberPageContext()
    // Check if user is already authenticated and redirect to Profile
    LaunchedEffect(Unit) {
        val firebaseUser = AuthRepository.getCurrentUser()
        if (firebaseUser != null && !firebaseUser.isAnonymous) {
            pageContext.router.navigateTo("/profil")
        }
    }

    // Reset state on mode switch
    LaunchedEffect(isLoginMode) {
        viewModel.resetState()
        emailError = ""
        passwordError = ""
    }

    // Handle navigation effects from ViewModel
    LaunchedEffect(Unit) {
        viewModel.navigationEffect.collect { effect ->
            when (effect) {
                is AuthNavigationEffect.NavigateToProfile -> {
                    pageContext.router.navigateTo("/profil")
                }
            }
        }
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .then(authPageLayoutStyle.toModifier()),
        contentAlignment = Alignment.Center
    ) {
        val colorMode = ColorMode.current

        // Left side (dark background)
        Box(
            modifier = Modifier
                .fillMaxHeight()
                .backgroundColor(AppTheme.colors.section)
                .justifyContent(JustifyContent.Center)
                .alignItems(AlignItems.Center)
                .then(authLeftPanelStyle.toModifier()),
            contentAlignment = Alignment.Center
        ) {
            Column(
                modifier = headerAuthPaddingStyle.toModifier().fillMaxSize(),
                horizontalAlignment = Alignment.Start,
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                // Logo and company name
                if (!isOnboarding) {
                    Link("/") {
                        Image(
                            if (colorMode.isLight) {
                                "logo-light.png"
                            } else {
                                "logo-dark.png"
                            },
                            "rezident.info Logo",
                            headerLogoStyle.toModifier()
                                .display(DisplayStyle.Block)
                        )
                    }
                }

                Spacer(Modifier.height(200.px))

                // Testimonial
                Column(Modifier.padding(leftRight = 8.px)) {
                    SpanText(
                        text = "Dermatologia, radiologia și psihiatria, în topul specialităților medicale preferate de rezidenți.”",
                        AppTheme.typography.body1.toModifier()
                            .color(AppTheme.colors.text)
                            .fontStyle(FontStyle.Italic)
                    )

                    SpanText(
                        text = "Articol publicat pe HotNews.ro",
                        AppTheme.typography.body1Medium.toModifier()
                            .color(AppTheme.colors.text)
                            .margin(top = 16.px)
                    )
                }
            }
        }

        // Right side (light background with form)
        Box(
            modifier = Modifier
                .fillMaxHeight()
                .backgroundColor(AppTheme.colors.background)
                .justifyContent(JustifyContent.Center)
                .alignItems(AlignItems.Center)
                .then(authRightPanelStyle.toModifier()),
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                Row(
                    headerAuthPaddingStyle.toModifier().align(Alignment.TopCenter).fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Link("/") {
                        Image(
                            if (colorMode.isLight) {
                                "logo-light.png"
                            } else {
                                "logo-dark.png"
                            },
                            "rezident.info Logo",
                            headerLogoStyle.toModifier()
                                .displayUntil(Breakpoint.MD)
                                .display(DisplayStyle.Block)
                        )
                    }

                    // Login/Create account link at top right
                    Button(
                        onClick = {
                            isLoginMode = !isLoginMode
                        },
                        modifier = Modifier
                            .backgroundColor(Colors.Transparent)
                            .border(1.px, LineStyle.Solid, AppTheme.colors.text)
                            .borderRadius(AppTheme.shapes.mediumRound),
                        size = ButtonSize.MD
                    ) {
                        SpanText(
                            if (isLoginMode)
                                "Creează cont"
                            else
                                "Ai deja cont?",
                            AppTheme.typography.body1Medium.toModifier()
                                .fillMaxSize()
                                .textAlign(TextAlign.Center)
                                .color(AppTheme.colors.text)
                        )
                    }
                }

                // Force recomposition when mode changes
                key(isLoginMode) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        modifier = authFormContainerStyle.toModifier()
                            .animation(
                                fadeAnimation.toAnimation(
                                    duration = 500.ms,
                                    timingFunction = AnimationTimingFunction.Linear
                                )
                            )
                    ) {
                        // Header
                        SpanText(
                            text = if (isLoginMode) "Intră în contul tău" else "Vizualizează recomandările de specialitate în contul tău",
                            if (isLoginMode) {
                                AppTheme.typography.h1.toModifier()
                            } else {
                                AppTheme.typography.h2.toModifier()
                            }
                                .margin(bottom = 8.px, leftRight = 8.px)
                                .color(AppTheme.colors.text)
                                .textAlign(TextAlign.Center)
                        )

                        if (isLoginMode) {
                            SpanText(
                                text = "Pentru a accesa recomandările de specialitate",
                                AppTheme.typography.body1.toModifier()
                                    .color(AppTheme.colors.textTertiary)
                                    .margin(bottom = 32.px)
                                    .textAlign(TextAlign.Center)
                            )
                        } else {
                            Spacer(Modifier.height(20.px))
                        }

                        // Email field
                        InputField(
                            inputType = InputType.Email,
                            value = email,
                            placeholder = "Adresa ta de e-mail",
                            isValid = emailError.isEmpty(),
                            autoComplete = AutoComplete.email,
                            onBlur = {
                                emailError = if (!isValidEmail(email) && email.isNotBlank()) {
                                    "Introdu o adresă de e-mail validă"
                                } else {
                                    ""
                                }
                            }
                        ) {
                            email = it
                        }

                        if (emailError.isNotEmpty()) {
                            SpanText(
                                text = emailError,
                                AppTheme.typography.body1.toModifier()
                                    .color(AppTheme.colors.error)
                                    .fontSize(12.px)
                                    .margin(top = 4.px)
                                    .alignSelf(AlignSelf.Start)
                            )

                            Spacer(Modifier.height(8.px))
                        } else {
                            Spacer(Modifier.height(12.px))
                        }

                        // Password field
                        InputField(
                            inputType = InputType.Password,
                            value = password,
                            placeholder = "Parola ta",
                            isValid = passwordError.isEmpty(),
                            autoComplete = if (isLoginMode) AutoComplete.currentPassword else AutoComplete.newPassword,
                            onBlur = {
                                passwordError = if (!isLoginMode && password.length < 8 && password.isNotEmpty()) {
                                    "Parola trebuie să conțină cel puțin 8 caractere"
                                } else {
                                    ""
                                }
                            }
                        ) {
                            password = it
                        }

                        if (passwordError.isNotEmpty()) {
                            SpanText(
                                text = passwordError,
                                AppTheme.typography.body1.toModifier()
                                    .color(AppTheme.colors.error)
                                    .fontSize(12.px)
                                    .margin(top = 4.px)
                                    .alignSelf(AlignSelf.Start)
                            )

                            Spacer(Modifier.height(8.px))
                        } else {
                            Spacer(Modifier.height(12.px))
                        }

                        // Login/Create account button
                        Button(
                            onClick = {
                                if (email.isNotEmpty() && isValidEmail(email)) {
                                    if (isLoginMode) {
                                        viewModel.signInWithEmail(email, password)
                                    } else {
                                        viewModel.registerWithEmail(email, password)
                                    }
                                } else {
                                    if (!isValidEmail(email)) {
                                        emailError = "Introdu o adresă de e-mail validă"
                                    }
                                    if (password.length < 8) {
                                        passwordError = "Parola trebuie să conțină cel puțin 8 caractere"
                                    }
                                }
                            },
                            modifier = Modifier
                                .fillMaxWidth()
                                .border(1.px, LineStyle.Solid, AppTheme.colors.text)
                                .backgroundColor(Color.transparent)
                                .color(AppTheme.colors.text)
                                .borderRadius(AppTheme.shapes.mediumRound),
                            enabled = authUiState != AuthUiState.Loading
                        ) {
                            if (authUiState == AuthUiState.Loading) {
                                SpanText(
                                    text = "Se procesează...",
                                    modifier = Modifier.color(AppTheme.colors.text)
                                )
                            } else {
                                SpanText(
                                    text = if (isLoginMode) "Conectează-te" else "Creează contul",
                                    modifier = Modifier.color(AppTheme.colors.text)
                                )
                            }
                        }

                        if (authUiState is AuthUiState.Error) {
                            SpanText(
                                text = (authUiState as AuthUiState.Error).message,
                                AppTheme.typography.body2.toModifier()
                                    .fillMaxWidth()
                                    .color(AppTheme.colors.error)
                                    .margin(top = 4.px)
                                    .alignSelf(AlignSelf.Start)
                            )
                            Spacer(Modifier.height(8.px))
                        }
                        if (authUiState == AuthUiState.Success) {
                            SpanText(
                                text = if (isLoginMode) "Autentificare reușită!" else "Cont creat cu succes!",
                                AppTheme.typography.body1.toModifier()
                                    .color(AppTheme.colors.accent)
                                    .fontSize(12.px)
                                    .margin(top = 4.px)
                                    .alignSelf(AlignSelf.Start)
                            )
                            Spacer(Modifier.height(8.px))
                        }

                        // OR Divider
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .margin(topBottom = 24.px)
                                .gap(16.px)
                                .alignItems(AlignItems.Center),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            HorizontalDivider(
                                modifier = Modifier.weight(1f)
                            )

                            SpanText(
                                text = "SAU",
                                AppTheme.typography.body1.toModifier()
                                    .fontSize(12.px)
                                    .color(AppTheme.colors.textTertiary)
                            )

                            HorizontalDivider(
                                modifier = Modifier.weight(1f)
                            )
                        }

                        // Google Sign-In Button
                        GoogleSignInButton(
                            modifier = Modifier.fillMaxWidth(),
                            isLoading = authUiState == AuthUiState.Loading,
                            enabled = authUiState != AuthUiState.Loading
                        )

                        // Terms and Privacy Policy
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .margin(top = 24.px),
                            contentAlignment = Alignment.Center
                        ) {
                            Div(Modifier.color(AppTheme.colors.textTertiary).toAttrs()) {
                                SpanText(
                                    text = "Continuând, ești de acord cu ",
                                    AppTheme.typography.body1.toModifier()
                                        .fontSize(14.px)
                                )

                                Link("/termeni") {
                                    SpanText(
                                        text = "Termenii și Condițiile",
                                        AppTheme.typography.body1.toModifier()
                                            .fontSize(14.px)
                                            .textDecorationLine(TextDecorationLine.Underline)
                                    )
                                }

                                SpanText(
                                    text = " și ",
                                    AppTheme.typography.body1.toModifier()
                                        .fontSize(14.px)
                                )

                                Link("/confidentialitate") {
                                    SpanText(
                                        text = "Politica de Confidențialitate",
                                        AppTheme.typography.body1.toModifier()
                                            .fontSize(14.px)
                                            .textDecorationLine(TextDecorationLine.Underline)
                                    )
                                }

                                SpanText(
                                    text = ".",
                                    AppTheme.typography.body1.toModifier()
                                        .fontSize(14.px)
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

val headerAuthPaddingStyle = CssStyle {
    base {
        Modifier.padding {
            left(14.px)
            right(14.px)
            top(5.px)
            bottom(24.px)
        }
    }
    // Adjust padding for smaller screens
    Breakpoint.SM {
        Modifier.padding(left = 16.px, top = 10.px, right = 16.px, bottom = 36.px)
    }

    Breakpoint.MD {
        Modifier.padding(left = 16.px, top = 10.px, right = 16.px, bottom = 36.px)
    }
}

val fadeAnimation = Keyframes {
    from { Modifier.opacity(0) }
    to { Modifier.opacity(1) }
}

// Style for the main page layout (flex direction based on breakpoint)
val authPageLayoutStyle = CssStyle {
    base { // Mobile-first: Column layout
        Modifier
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
    }
    Breakpoint.MD { // Desktop: Row layout
        Modifier
            .flexDirection(FlexDirection.Row)
    }
}

// Style for the left panel (hidden on mobile)
val authLeftPanelStyle = CssStyle {
    base { // Hidden on mobile
        Modifier.display(DisplayStyle.None)
    }
    Breakpoint.MD { // Visible on desktop
        Modifier
            .display(DisplayStyle.Flex)
            .width(50.percent)
    }
}

// Style for the right panel (form)
val authRightPanelStyle = CssStyle {
    base { // Full width on mobile
        Modifier
            .width(100.percent)
    }
    Breakpoint.MD { // Half width on desktop
        Modifier
            .width(50.percent)
            .padding(topBottom = 16.px) // Reset padding for desktop
    }
}

// Style for the form container inside the right panel
val authFormContainerStyle = CssStyle {
    base { // Mobile: slightly less width than full screen
        Modifier.fillMaxWidth(90.percent)
    }
    Breakpoint.MD { // Desktop: specific width constraints
        Modifier
            .fillMaxWidth(80.percent)
            .maxWidth(600.px)
    }
}

fun isValidEmail(email: String): Boolean {
    val emailRegex = "^[A-Za-z](.*)([@]{1})(.{1,})(\\.)(.{1,})"
    return email.matches(emailRegex.toRegex())
}